import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ADDON_ENTITLEMENT_KEYS, ADDON_TYPES } from '@d3-moveis/shared';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useAddons, useCreateAddon, useSyncAddon, useUpdateAddon } from '../hooks/api/useAddons';

const emptyForm = {
  slug: '',
  name: '',
  type: 'one_time',
  entitlementKey: 'ai_render_pack',
  creditAmount: '',
  priceCents: 0,
  isActive: true,
};

function formatPrice(cents, currency) {
  const value = (cents ?? 0) / 100;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: currency || 'BRL' }).format(value);
}

export function AddonsPage() {
  const { t } = useTranslation();
  const { data: addons, isLoading, error } = useAddons();
  const createAddon = useCreateAddon();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [syncMessage, setSyncMessage] = useState('');

  const updateAddon = useUpdateAddon(editingAddon?.uuid ?? '');
  const syncAddon = useSyncAddon(editingAddon?.uuid ?? '');

  const sortedAddons = useMemo(() => {
    if (!Array.isArray(addons)) return [];
    return [...addons].sort((a, b) => a.slug.localeCompare(b.slug));
  }, [addons]);

  const openCreate = () => {
    setEditingAddon(null);
    setForm(emptyForm);
    setSyncMessage('');
    setDialogOpen(true);
  };

  const openEdit = (addon) => {
    setEditingAddon(addon);
    setForm({
      slug: addon.slug,
      name: addon.name,
      type: addon.type,
      entitlementKey: addon.entitlementKey,
      creditAmount: addon.creditAmount ?? '',
      priceCents: addon.priceCents,
      isActive: addon.isActive,
    });
    setSyncMessage('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (!createAddon.isPending && !updateAddon.isPending) {
      setDialogOpen(false);
    }
  };

  const handleSave = async () => {
    const creditAmount = form.creditAmount === '' || form.creditAmount === null ? null : Number(form.creditAmount);

    try {
      if (editingAddon) {
        await updateAddon.mutateAsync({
          name: form.name,
          type: form.type,
          entitlementKey: form.entitlementKey,
          creditAmount,
          priceCents: Number(form.priceCents),
          isActive: form.isActive,
        });
      } else {
        await createAddon.mutateAsync({
          slug: form.slug.trim().toLowerCase(),
          name: form.name.trim(),
          type: form.type,
          entitlementKey: form.entitlementKey,
          creditAmount,
          priceCents: Number(form.priceCents),
          isActive: form.isActive,
        });
      }
      setDialogOpen(false);
    } catch {
      // mutation error surfaces via isError if needed
    }
  };

  const handleSync = async () => {
    if (!editingAddon) return;
    const result = await syncAddon.mutateAsync();
    setSyncMessage(result?.message ?? t('addons.syncPending'));
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">{t('addons.title')}</Typography>
        <Button variant="contained" onClick={openCreate} sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}>
          {t('addons.create')}
        </Button>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error.message || t('common.error')}</Alert>}

      {!isLoading && !error && (
        <Paper>
          {sortedAddons.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">{t('addons.empty')}</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('addons.slug')}</TableCell>
                    <TableCell>{t('addons.name')}</TableCell>
                    <TableCell>{t('addons.type')}</TableCell>
                    <TableCell>{t('addons.entitlement')}</TableCell>
                    <TableCell>{t('addons.credits')}</TableCell>
                    <TableCell>{t('addons.price')}</TableCell>
                    <TableCell align="right"> </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedAddons.map((addon) => (
                    <TableRow key={addon.uuid} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {addon.slug}
                        </Typography>
                      </TableCell>
                      <TableCell>{addon.name}</TableCell>
                      <TableCell>{t(`addons.type.${addon.type}`)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {addon.entitlementKey}
                        </Typography>
                      </TableCell>
                      <TableCell>{addon.creditAmount ?? '—'}</TableCell>
                      <TableCell>{formatPrice(addon.priceCents, addon.currency)}</TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={addon.isActive ? t('addons.isActive') : '—'} sx={{ mr: 1 }} />
                        <Button size="small" onClick={() => openEdit(addon)}>
                          {t('addons.edit')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingAddon ? t('addons.edit') : t('addons.create')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {!editingAddon && (
            <TextField
              label={t('addons.slug')}
              value={form.slug}
              onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
              required
              fullWidth
            />
          )}
          <TextField
            label={t('addons.name')}
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            required
            fullWidth
          />
          <TextField
            select
            label={t('addons.type')}
            value={form.type}
            onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
            fullWidth
          >
            {ADDON_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {t(`addons.type.${type}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('addons.entitlement')}
            value={form.entitlementKey}
            onChange={(e) => setForm((prev) => ({ ...prev, entitlementKey: e.target.value }))}
            fullWidth
          >
            {ADDON_ENTITLEMENT_KEYS.map((key) => (
              <MenuItem key={key} value={key}>
                {key}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('addons.credits')}
            type="number"
            value={form.creditAmount}
            onChange={(e) => setForm((prev) => ({ ...prev, creditAmount: e.target.value }))}
            fullWidth
            helperText={t('addons.creditsHint')}
          />
          <TextField
            label={t('addons.price')}
            type="number"
            value={form.priceCents}
            onChange={(e) => setForm((prev) => ({ ...prev, priceCents: e.target.value }))}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
            }
            label={t('addons.isActive')}
          />
          {syncMessage && (
            <Alert severity="info" onClose={() => setSyncMessage('')}>
              {syncMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {editingAddon && (
            <Button onClick={handleSync} disabled={syncAddon.isPending}>
              {t('addons.sync')}
            </Button>
          )}
          <Button onClick={closeDialog}>{t('addons.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={createAddon.isPending || updateAddon.isPending}>
            {t('addons.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
